import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase/firebaseConfig';
import {
  deleteTransaction,
  deleteRecurringTransaction,
  markTransactionPaid,
  partialPayTransaction,
  skipRecurringTransactionOccurrence
} from '../firebase/firebaseUtils';
import { Button, Card, Table, Thead, Tbody, Tr, Th, Td, TableContainer, ToggleWrapper, ToggleInput, ToggleSlider, Dropdown, DropdownContent, SplitButtonLeft, SplitButtonRight, Input, InputWrapper, ClearButton } from './StyledComponents';
import EditTransactionModal from './EditTransactionModal';
import AddTransactionModal from './AddTransactionModal';
import DeleteTransactionModal from './DeleteTransactionModal';
import PartialPayModal from './PartialPayModal';
import { createTransactionWithRecurrence, saveTransactionEdits } from '../utils/transactionHelpers';

const Transactions = ({ userId, accounts, birthDate, settings }) => {
  const [transactions, setTransactions] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPartialPayModalOpen, setIsPartialPayModalOpen] = useState(false);
  const [addModalPrefill, setAddModalPrefill] = useState(null);
  const [isAddButtonDragOver, setIsAddButtonDragOver] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [partialPayTransactionData, setPartialPayTransactionData] = useState(null);
  const [showPaid, setShowPaid] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [visibleCount, setVisibleCount] = useState(settings?.transactionsPageSize || 30);
  const containerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const dropdownRefs = useRef({});

  const closeDropdown = () => setOpenDropdownId(null);

  const toggleDropdown = (id) => {
    setOpenDropdownId(prev => (prev === id ? null : id));
  };
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  useEffect(() => {
    if (!userId) return;
  
    const transactionsRef = ref(database, `users/${userId}/transactions`);
    onValue(transactionsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const allTransactions = [];
  
      Object.entries(data).forEach(([key, value]) => {
        if (value.childTransactions) {
          const parentRecurrence = value.recurrence;
          const parentRecurrenceEndDate = value.recurrenceEndDate;
          Object.entries(value.childTransactions).forEach(([childKey, childValue]) => {
            allTransactions.push({ id: childKey, parentId: key, recurrence: parentRecurrence, recurrenceEndDate: parentRecurrenceEndDate, ...childValue });
          });
        } else {
          allTransactions.push({ id: key, ...value });
        }
      });

      allTransactions.sort((a, b) => {
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return b.amount - a.amount;
      });
      setTransactions(allTransactions);
    });
  }, [userId]);


  useEffect(() => {
    setVisibleCount(settings?.transactionsPageSize || 30);
  }, [settings, filterText, showPaid, transactions]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) => prev + (settings?.transactionsPageSize || 30));
        }
      });
    }, { root: containerRef.current, rootMargin: '100px' });
    const el = loadMoreRef.current;
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [settings, filterText, showPaid, transactions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openDropdownId) return;
      const dropdownEl = dropdownRefs.current[openDropdownId];
      if (dropdownEl && dropdownEl.contains(event.target)) {
        return;
      }
      setOpenDropdownId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openDropdownId]);
  
  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setAddModalPrefill(null);
  };

  const openAddModal = (prefill = null) => {
    setAddModalPrefill(prefill);
    setIsAddModalOpen(true);
  };

  const handleAddTransaction = async (newTransaction) => {
    try {
      await createTransactionWithRecurrence(userId, newTransaction);
    } catch (error) {
      console.error('Error in handleAddTransaction:', error);
    }
  };

  const supportsReconciliationPayload = (event) => {
    if (!event?.dataTransfer?.types) return false;
    const types = Array.from(event.dataTransfer.types);
    return types.includes('application/x-reconciliation-transaction') || types.includes('application/json');
  };

  const parseReconciliationDropPayload = (event) => {
    if (!event?.dataTransfer) return null;
    let raw = event.dataTransfer.getData('application/x-reconciliation-transaction');
    if (!raw) {
      raw = event.dataTransfer.getData('application/json');
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.type === 'reconciliation-transaction') {
        return parsed;
      }
    } catch (error) {
      console.warn('Unable to parse reconciliation payload for add transaction drop', error);
    }
    return null;
  };

  const sanitizeDropDate = (value) => {
    const today = new Date().toISOString().split('T')[0];
    if (!value && value !== 0) return today;
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    if (typeof value === 'string') {
      const match = value.match(/\d{4}-\d{2}-\d{2}/);
      if (match) return match[0];
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return today;
  };

  const handleAddButtonDrop = (event) => {
    setIsAddButtonDragOver(false);
    if (!supportsReconciliationPayload(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const payload = parseReconciliationDropPayload(event);
    if (!payload) return;

    const numericAmount = Number(payload.amount);
    const hasAmount = Number.isFinite(numericAmount);

    const prefill = {
      description: payload.description || payload.rawDescription || payload.accountName || '',
      amount: hasAmount ? numericAmount : '',
      date: sanitizeDropDate(payload.date),
      fromAccount: hasAmount && numericAmount < 0 ? (payload.accountId || '') : '',
      toAccount: hasAmount && numericAmount > 0 ? (payload.accountId || '') : '',
      rawDescription: payload.rawDescription || '',
    };

    openAddModal(prefill);
  };

  const handleAddButtonDragOver = (event) => {
    if (!supportsReconciliationPayload(event)) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    setIsAddButtonDragOver(true);
  };

  const handleAddButtonDragLeave = () => {
    setIsAddButtonDragOver(false);
  };

  const handleAddButtonClick = () => {
    openAddModal(null);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setIsEditModalOpen(true);
    closeDropdown();
  };

  const handleDeleteTransactionClick = (transaction) => {
    setDeletingTransaction(transaction);
    setIsDeleteModalOpen(true);
    closeDropdown();
  };

  const handleDeleteTransaction = async (deleteMode) => {
    const { id, parentId } = deletingTransaction;

    try {
      if (deleteMode === 'single' || !parentId) {
        await deleteTransaction(userId, id, parentId);
      } else if (deleteMode === 'series' && parentId) {
        await deleteRecurringTransaction(userId, parentId);
      }

      setDeletingTransaction(null);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting transaction: ", error);
    }
  };

  const handleMarkPaid = async (transaction) => {
    const { id, parentId, fromAccount, toAccount, amount } = transaction;

    try {
      await markTransactionPaid(userId, id, parentId, fromAccount, toAccount, amount);
    } catch (error) {
      console.error('Error marking transaction paid: ', error);
    } finally {
      closeDropdown();
    }
  };

  const handlePartialPayClick = (transaction) => {
    setPartialPayTransactionData(transaction);
    setIsPartialPayModalOpen(true);
    closeDropdown();
  };

  const closePartialPayModal = () => {
    setIsPartialPayModalOpen(false);
    setPartialPayTransactionData(null);
  };

  const handlePartialPay = async (amount, date, description) => {
    if (!partialPayTransactionData) return;
    const { id, parentId, fromAccount, toAccount } = partialPayTransactionData;

    await partialPayTransaction(userId, id, parentId, fromAccount, toAccount, amount, date, description);
  };

  const handleSkipTransaction = async (transaction) => {
    const { id, parentId } = transaction;
    if (!parentId) return;

    try {
      await skipRecurringTransactionOccurrence(userId, id, parentId);
    } catch (error) {
      console.error('Error skipping transaction: ', error);
    } finally {
      closeDropdown();
    }
  };

  const handleSaveTransaction = async (updatedTransaction) => {
    try {
      await saveTransactionEdits(userId, updatedTransaction);
      setIsEditModalOpen(false);
      setEditingTransaction(null);
    } catch (error) {
      console.error("Error updating transaction: ", error);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const filteredTransactions = (showPaid ? transactions : transactions.filter(t => !t.isPaid))
    .filter(t => {
      const text = filterText.toLowerCase();
      return (
        t.description.toLowerCase().includes(text) ||
        (accounts[t.fromAccount]?.name || '').toLowerCase().includes(text) ||
        (accounts[t.toAccount]?.name || '').toLowerCase().includes(text)
      );
    });
  const displayedTransactions = filteredTransactions.slice(0, visibleCount);

  return (
    <>
      <EditTransactionModal
        transaction={editingTransaction}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveTransaction}
        accounts={accounts}
        userId={userId}
      />
      <AddTransactionModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        onSave={handleAddTransaction}
        accounts={accounts}
        birthDate={birthDate}
        userId={userId}
        initialTransaction={addModalPrefill}
      />
      <DeleteTransactionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteTransaction}
        transaction={deletingTransaction}
      />
      <PartialPayModal
        isOpen={isPartialPayModalOpen}
        onClose={closePartialPayModal}
        onConfirm={handlePartialPay}
        transaction={partialPayTransactionData}
        initialDescription={partialPayTransactionData?.description}
      />
      <Card>
        <h2 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Transactions</span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <InputWrapper style={{ marginRight: '10px', width: '150px' }}>
              <Input
                placeholder="Filter"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
              />
              {filterText && (
                <ClearButton onClick={() => setFilterText('')} aria-label="Clear filter">&times;</ClearButton>
              )}
            </InputWrapper>
            <div style={{ marginRight: '10px', fontSize: '0.8em', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '5px' }}>Show Paid</span>
              <ToggleWrapper title="Toggle showing paid transactions">
                <ToggleInput
                  type="checkbox"
                  checked={showPaid}
                  onChange={() => setShowPaid(!showPaid)}
                />
                <ToggleSlider />
              </ToggleWrapper>
            </div>
            <Button
              title="Add New Transaction"
              onClick={handleAddButtonClick}
              onDragOver={handleAddButtonDragOver}
              onDragEnter={handleAddButtonDragOver}
              onDragLeave={handleAddButtonDragLeave}
              onDrop={handleAddButtonDrop}
              style={isAddButtonDragOver ? { outline: '2px dashed #4CAF50' } : undefined}
            >
              +
            </Button>
          </span>
        </h2>
        <TableContainer ref={containerRef}>
          <Table>
            <Thead>
              <Tr>
                <Th>Description</Th>
                <Th>Amount</Th>
                <Th>Date</Th>
                <Th>From Account</Th>
                <Th>To Account</Th>
                <Th>Recurrence</Th>
                {showPaid && <Th>Paid</Th>}
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
            {displayedTransactions.map((transaction) => (
                <Tr key={transaction.id} onClick={() => handleEditTransaction(transaction)}>
                  <Td>{transaction.description}</Td>
                  <Td>{formatCurrency(transaction.amount)}</Td>
                  <Td>{formatDate(transaction.date)}</Td>
                  <Td>{accounts[transaction.fromAccount]?.name}</Td>
                  <Td>{accounts[transaction.toAccount]?.name}</Td>
                  <Td>{transaction.recurrence ? transaction.recurrence : 'None'}</Td> {/* Display 'None' if no recurrence */}
                  {showPaid && <Td>{transaction.isPaid ? 'Yes' : 'No'}</Td>}
                  <Td onClick={e => e.stopPropagation()}>
                    <Dropdown
                      ref={(el) => {
                        if (el) {
                          dropdownRefs.current[transaction.id] = el;
                        } else {
                          delete dropdownRefs.current[transaction.id];
                        }
                      }}
                    >
                      {!transaction.isPaid && (
                        <SplitButtonLeft onClick={() => handleMarkPaid(transaction)}>Mark Paid</SplitButtonLeft>
                      )}
                      <SplitButtonRight onClick={() => toggleDropdown(transaction.id)}>▾</SplitButtonRight>
                      {openDropdownId === transaction.id && (
                        <DropdownContent onClick={e => e.stopPropagation()}>
                          {!transaction.isPaid && (
                            <Button onClick={() => handlePartialPayClick(transaction)}>Partial Pay</Button>
                          )}
                          {!transaction.isPaid && transaction.parentId && (
                            <Button onClick={() => handleSkipTransaction(transaction)}>Skip</Button>
                          )}
                          <Button onClick={() => handleEditTransaction(transaction)}>Edit</Button>
                          <Button onClick={() => handleDeleteTransactionClick(transaction)}>Delete</Button>
                        </DropdownContent>
                      )}
                    </Dropdown>
                  </Td>
                </Tr>
              ))}
              {visibleCount < filteredTransactions.length && (
                <Tr ref={loadMoreRef}><Td colSpan={showPaid ? 8 : 7}></Td></Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
};

export default Transactions;
